import React from "react";
import { WithStoryProps, withStory } from "@storyofams/storyblok-toolkit";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Script from "next/script";
import { Description } from "~components/product/Description";
import { Header } from "~components/product/Header";
import { Recomendations } from "~components/product/Recomendations";
import { loadProductBundle } from "~lib/loadProductBundle";
import { storyblokSdk } from "~lib/storyblok/client";
import {
  Container,
  ProductStructuredData,
  Reviews,
  Seo,
} from "../../components";
import { CallToAction } from "../../components/common/Story/blocks/CallToAction";
import {
  enhancedStaticProps,
  formatShopifyIdForTracking,
  loadProducts,
  withLayout,
} from "../../lib";
import { getShopifySdk } from "../../lib/shopify/client";
import { ProductByHandleQuery } from "../../lib/shopify/sdk";

interface ProductDetailPageProps extends WithStoryProps {
  product: ProductByHandleQuery["productByHandle"];
  productDetails?: any;
  propertys?: any;
  bundles?: any;
}

const ProductDetailPage: NextPage<ProductDetailPageProps> = ({
  product,
  story,
  productDetails,
  propertys,
  bundles,
}) => {
  const noIndex = product.metafields.find(
    (item) => item?.key === "hide" && item?.value === "true"
  );
  const { content } = productDetails || {};
  return (
    <>
      <Script
        dangerouslySetInnerHTML={{
          __html: `var SKIP_CART=true;
            var FORCE_DOMAIN = true;
            var VIDEOWISE_FAST_INLINE_VIDEO_PLAYER = true;
            var Shopify = { 
              shop: 'natural-heroes-nl.myshopify.com', currency:{active:'EUR', rate:'1.0'}
            };

            var __st = {
              rid: ${formatShopifyIdForTracking(product.id)},
              p: 'product'
            };`,
        }}
      />
      <Script
        defer
        src="https://assets.videowise.com/vendors.js.gz"
        id="videowise-vendors-js"
      />
      <Script
        defer
        src="https://assets.videowise.com/client.js.gz"
        id="videowise-client-js"
      />
      <Seo
        noIndex={story?.content.noIndex || noIndex}
        title={product?.seo?.title || product.title}
        defaultTemplate
        description={product?.seo?.description || product.description}
        image={product?.images?.edges?.[0]?.node?.image?.originalSrc}
        canonical={`https://www.naturalheroes.nl/products/${product.handle}`}
      />
      <ProductStructuredData
        product={product}
        sku={product?.variants?.edges[0]?.node?.sku}
      />
      <Container py={0} px={2} mt={0.5} space={0} mb={["40px", 0]}>
        <Header product={product} story={story} content={content} />
      </Container>
      <Container withOverflowHidden space={3} pt={0} px={2}>
        <Description product={product} story={story} bundles={bundles} />
        {!story?.content?.isReviewsDisabled && (
          <Reviews
            propertys={propertys}
            productId={product?.id}
            productTitle={product?.title}
          />
        )}
        {!story?.content.isProductRecomendationsDisabled && (
          <Recomendations content={content} id={product.id} />
        )}
      </Container>
      <CallToAction
        footerColor={story?.content?.footer_color?.color}
        content={content?.cta2?.content}
        mb={[10, 5]}
      />
    </>
  );
};

export default withLayout(withStory(ProductDetailPage));

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: "blocking",
    paths: [],
  };
};

export const getStaticProps: GetStaticProps<ProductDetailPageProps> =
  enhancedStaticProps(async ({ params, sdk }) => {
    const { handle } = params;
    const shopifySdk = getShopifySdk();
    let product;
    let notFound;
    let story;
    let propertys;
    let bundles = null;
    const removeHiddenVariants = (prod) => {
      const filteredVariants = prod?.variants?.edges?.filter(({ node }) => {
        const metafieldNode = node?.metafields[0];
        if (metafieldNode?.key === "hide") {
          return metafieldNode?.value !== "true";
        }
        return node?.value !== "true";
      });
      return {
        ...prod,
        variants: { ...prod.variants, edges: filteredVariants },
      };
    };

    try {
      product = (await shopifySdk.productByHandle({ handle: `${handle}` }))
        .productByHandle;
    } catch (err) {
      console.log(err);
    }
    if (!product) {
      notFound = true;

      return {
        props: {},
        notFound: true,
      };
    }
    const pureProduct = removeHiddenVariants(product);
    if (!pureProduct.variants?.edges?.length) {
      notFound = true;

      return {
        props: {},
        notFound: true,
      };
    }
    try {
      story = (
        await sdk.productDetailsItem({
          slug: `products/${String(handle)}`,
        })
      )?.ProductdetailsItem;
    } catch (err) {
      console.log(err);
    }
    try {
      propertys = (await storyblokSdk.userProperys({ slug: "user-propertys" }))
        ?.UserpropertysItem.content;
    } catch (err) {
      console.log(err);
    }
    const [{ ProductdetailItem }] = await Promise.all([
      sdk.pdp({ slug: "/products/product-detail" }),
    ]);
    try {
      bundles = await loadProductBundle(product);
    } catch (err) {
      console.log(err);
    }
    try {
      await loadProducts(story, "nl");
    } catch {}

    return {
      props: {
        product: pureProduct,
        story: story || null,
        productDetails: ProductdetailItem,
        propertys,
        bundles,
      },
      notFound,
      revalidate: process.env.HOST === "dev.naturalheroes.nl" ? 1 : 7200,
    };
  });
